import React, { useEffect, useState, useMemo } from 'react';
import { Box, Button, TextField, InputAdornment, Chip, IconButton, Tooltip, MenuItem, Select, Switch, FormControlLabel, Autocomplete, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Drawer } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { SearchIcon } from 'lucide-react';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchLedgers, fetchGroupCategories, fetchPrimaryGroups, deleteLedger } from '../../store/coaStore/coaThunk';
import { ApiStatus, defaultRowsPerPageOptions } from '../../utils/api.types';
import CustomDataGrid from '../../components/DataGrid/DataGrid';
import LedgerForm from '../../components/COA/LedgerForm';
import DialogAddLedgerEntry from '../../components/COA/DialogAddLedgerEntry';
import { ILedger, IPrimaryGroup } from '../../utils/coa.types';

const Ledgers: React.FC = () => {
  const dispatch = useAppDispatch();
  const { ledgers, ledgersTotal, groupCategories, primaryGroups, status } = useAppSelector(state => state.coa);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [groupCategoryId, setGroupCategoryId] = useState('');
  const [primaryGroupId, setPrimaryGroupId] = useState('');
  const [selectedPrimaryGroup, setSelectedPrimaryGroup] = useState<IPrimaryGroup | null>(null);
  const [filterOwn, setFilterOwn] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [formOpen, setFormOpen] = useState(false);
  const [editData, setEditData] = useState<ILedger | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ledgerToDelete, setLedgerToDelete] = useState<ILedger | null>(null);
  const [entryFormOpen, setEntryFormOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchGroupCategories({ page: 0, limit: 1000 }));
    dispatch(fetchPrimaryGroups({ page: 0, limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(0);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  useEffect(() => {
    dispatch(fetchLedgers({ page, limit: pageSize, search: searchQuery, groupCategoryId, primaryGroupId, filterOwn }));
  }, [dispatch, page, pageSize, searchQuery, groupCategoryId, primaryGroupId, filterOwn]);

  const handleSearch = (value: string) => {
    setSearchInput(value);
  };

  const handleCreate = () => {
    setEditData(null);
    setFormOpen(true);
  };

  const handleEdit = (row: ILedger) => {
    setEditData(row);
    setFormOpen(true);
  };

  const handleDeleteClick = (row: ILedger) => {
    setLedgerToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (ledgerToDelete) {
      try {
        await dispatch(deleteLedger(ledgerToDelete._id)).unwrap();
        dispatch(fetchLedgers({ page, limit: pageSize, search: searchQuery, groupCategoryId, primaryGroupId, filterOwn }));
      } catch (error: any) {
        // Error toast is handled by Redux slice
      }
    }
    setDeleteDialogOpen(false);
    setLedgerToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setLedgerToDelete(null);
  };

  const handleFormClose = (refresh?: boolean) => {
    setFormOpen(false);
    setEditData(null);
    if (refresh) {
      dispatch(fetchLedgers({ page, limit: pageSize, search: searchQuery }));
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const formatAmount = (amount: number) => {
    if (!amount) return '-';
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'accountingCode',
      headerName: 'Code',
      flex: 0.6,
      renderCell: ({ row }: GridValueGetterParams) => row.accountingCode || '-'
    },
    {
      field: 'ledgerName',
      headerName: 'Name',
      flex: 1.8,
      renderCell: ({ row }: GridValueGetterParams) => {
        const name = row.ledgerName || row.name;
        return (
          <Tooltip title={name}>
            <strong>{name}</strong>
          </Tooltip>
        );
      }
    },
    {
      field: 'groupCategory',
      headerName: 'Category',
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Tooltip title={row.groupCategory?.name || '-'}>
          <Chip
            size="small"
            label={row.groupCategory?.name || '-'}
            style={{ borderRadius: 7, color: '#000', backgroundColor: '#f3e5f5', fontWeight: 500 }}
          />
        </Tooltip>
      )
    },
    {
      field: 'primaryGroup',
      headerName: 'Primary Group',
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Tooltip title={row.primaryGroup?.name || '-'}>
          <Chip
            size="small"
            label={row.primaryGroup?.name || '-'}
            style={{ borderRadius: 7, color: '#000', backgroundColor: '#e3f2fd', fontWeight: 500 }}
          />
        </Tooltip>
      )
    },
    {
      field: 'subGroup',
      headerName: 'Sub Group',
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Tooltip title={row.subGroup?.name || '-'}>
          <Chip
            size="small"
            label={row.subGroup?.name || '-'}
            style={{ borderRadius: 7, color: '#000', backgroundColor: '#fff3e0', fontWeight: 500 }}
          />
        </Tooltip>
      )
    },
    {
      field: 'totalDebit',
      headerName: 'Total Debit',
      flex: 0.9,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }: GridValueGetterParams) => (
        <div style={{ width: '100%', textAlign: 'right' }}>
          <span style={{ color: '#2e7d32', fontWeight: 500 }}>
            {formatAmount(row.totalDebit)}
          </span>
        </div>
      )
    },
    {
      field: 'totalCredit',
      headerName: 'Total Credit',
      flex: 0.9,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }: GridValueGetterParams) => (
        <div style={{ width: '100%', textAlign: 'right' }}>
          <span style={{ color: '#d32f2f', fontWeight: 500 }}>
            {formatAmount(row.totalCredit)}
          </span>
        </div>
      )
    },
    {
      field: 'balance',
      headerName: 'Balance',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }: GridValueGetterParams) => {
        const bal = row.balance || 0;
        if (bal === 0) return <div style={{ width: '100%', textAlign: 'right', color: '#666' }}>-</div>;
        const isDebit = bal > 0;
        return (
          <div style={{ width: '100%', textAlign: 'right' }}>
            <span style={{ color: isDebit ? '#2e7d32' : '#d32f2f', fontWeight: 600 }}>
              {formatAmount(Math.abs(bal))} {isDebit ? 'Dr' : 'Cr'}
            </span>
          </div>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }: GridValueGetterParams) => {
        // Check if ledger belongs to current account (can only edit/delete own ledgers, not global ones)
        const isGlobalLedger = !row.account;
        const canModify = !isGlobalLedger;

        return (
          <>
            <Tooltip title={!canModify ? "Cannot edit global ledger" : ""}>
              <span style={{ marginRight: 8 }}>
                <IconButton
                  onClick={() => canModify && handleEdit(row)}
                  disabled={!canModify}
                  style={{
                    color: canModify ? '#49a563' : '#9e9e9e',
                    backgroundColor: canModify ? '#edf6ef' : '#f5f5f5',
                    border: canModify ? '0.1px solid #49a563' : '0.1px solid #9e9e9e',
                    height: 35,
                    width: 35,
                    cursor: canModify ? 'pointer' : 'not-allowed'
                  }}
                  size="small"
                >
                  <EditIcon style={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={!canModify ? "Cannot delete global ledger" : ""}>
              <span>
                <IconButton
                  onClick={() => canModify && handleDeleteClick(row)}
                  disabled={!canModify}
                  style={{
                    color: canModify ? '#d32f2f' : '#9e9e9e',
                    backgroundColor: canModify ? '#ffebee' : '#f5f5f5',
                    border: canModify ? '0.1px solid #d32f2f' : '0.1px solid #9e9e9e',
                    height: 35,
                    width: 35,
                    cursor: canModify ? 'pointer' : 'not-allowed'
                  }}
                  size="small"
                >
                  <DeleteIcon style={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </>
        );
      }
    }
  ], []);

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", width: '100%', pt: 2, pb: 1, gap: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: '100%', gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            <TextField
              className='c-text-field-3'
              variant="standard"
              style={{ width: 300, float: 'left' }}
              size='small'
              placeholder='Search Ledgers...'
              value={searchInput}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ marginTop: -3 }} size={20} />
                  </InputAdornment>
                ),
                style: { fontSize: 14, fontWeight: 'normal' },
              }}
            />
            <Select
              name="groupCategory"
              displayEmpty
              disableUnderline
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              sx={{
                boxShadow: "none",
                width: 200,
                ml: '10px !important',
                pt: '0px !important',
                pl: '10px !important',
                '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                '& .MuiSelect-select': { p: 0 },
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
              }}
              className='c-text-field-3'
              variant="outlined"
              value={groupCategoryId}
              onChange={(e) => {
                setGroupCategoryId(e.target.value);
                setPage(0);
              }}
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: '#999' }}>All Categories</span>;
                }
                const category = groupCategories.find(cat => cat._id === selected);
                return category?.name || 'All Categories';
              }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {groupCategories.map((category) => (
                <MenuItem key={category._id} value={category._id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            <Autocomplete
              options={primaryGroups}
              getOptionLabel={(option) => option.name || ''}
              value={selectedPrimaryGroup}
              onChange={(event, newValue) => {
                setSelectedPrimaryGroup(newValue);
                setPrimaryGroupId(newValue?._id || '');
                setPage(0);
              }}
              sx={{
                width: 200,
                ml: '10px !important',
                '& .MuiOutlinedInput-root': {
                  padding: '0px !important',
                  paddingLeft: '10px !important',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiAutocomplete-input': {
                  padding: '0px !important',
                  fontSize: 14,
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="All Primary Groups"
                  variant="outlined"
                  className='c-text-field-3'
                />
              )}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={filterOwn}
                  onChange={(e) => {
                    setFilterOwn(e.target.checked);
                    setPage(0);
                  }}
                  sx={{
                    ml: 2,
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#488dff',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#488dff',
                    },
                  }}
                />
              }
              label="My Ledgers"
              sx={{ ml: 1, color: '#666' }}
            />

          </Box>
          {/* Add Journal Entry Button — right after My Ledgers toggle */}
          <Button
              startIcon={<SwapHorizIcon />}
              size='small'
              onClick={() => setEntryFormOpen(true)}
              style={{
                color: '#7c3aed',
                backgroundColor: '#fff',
                border: '0.5px solid #7c3aed',
                height: 35,
                borderRadius: 7,
                marginLeft: 12,
                whiteSpace: 'nowrap',
              }}
              variant="contained"
            >
              Add Journal Entry
          </Button>
          <Button
            startIcon={<AddIcon />}
            size='small'
            onClick={handleCreate}
            style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}
            variant="contained"
          >
            Create Ledger
          </Button>
        </Box>
      </Box>

      <Box sx={{
        height: "calc(100vh - 200px)", width: "100%",
        '& .MuiDataGrid-columnHeader[data-field="balance"]': {
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: 0,
            width: '1px',
            height: '100%',
            backgroundColor: '#9da7b7'
          }
        }
      }}>
        {ledgers.length === 0 && status !== ApiStatus.LOADING ? (
          <Paper
            elevation={0}
            sx={{
              height: '90%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fafafb',
              border: '2px dashed #e0e0e0',
              borderRadius: 2,
              p: 4,
              marginTop: '10px'
            }}
          >
            <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
              {/* Icon */}
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 3,
                  mt: 1
                }}
              >
                <AccountBalanceIcon sx={{ fontSize: 60, color: 'primary.main' }} />
              </Box>

              {/* Title */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: '#333',
                  mb: 1
                }}
              >
                No Ledgers Yet
              </Typography>

              {/* Description */}
              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  mb: 4,
                  lineHeight: 1.6
                }}
              >
                Start managing your accounts by creating your first ledger.
                Examples: Cash, Bank Account, Accounts Receivable, etc.
              </Typography>

              {/* CTA Button */}
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleCreate}
                sx={{
                  px: 4,
                  py: 2,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px) scale(1.02)',
                    boxShadow: (theme) => `0 8px 25px -8px ${theme.palette.primary.main}`,
                    '&::before': {
                      transform: 'translateX(0)',
                    }
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 100%)',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.6s ease',
                    zIndex: 1
                  },
                  '&:active': {
                    transform: 'translateY(-2px) scale(0.98)',
                  },
                  '& .MuiButton-startIcon': {
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover .MuiButton-startIcon': {
                    transform: 'rotate(360deg)',
                  }
                }}
              >
                Add Your First Ledger
              </Button>

              {/* Additional Help Text */}
              {/* <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 3,
                  mb: 4,
                  color: '#999'
                }}
              >
                Ledgers are individual accounts that track specific financial activities
              </Typography> */}
            </Box>
          </Paper>
        ) : (
          <CustomDataGrid
            columns={columns}
            rows={ledgers}
            rowCount={ledgersTotal}
            rowsPerPageOptions={defaultRowsPerPageOptions}
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            getRowId={(row) => row._id}
            loading={status === ApiStatus.LOADING}
          />
        )}
      </Box>

      <LedgerForm
        open={formOpen}
        onClose={handleFormClose}
        editData={editData}
      />

      {/* Add Ledger Entry Drawer */}
      <Drawer
        anchor="right"
        open={entryFormOpen}
        disableEscapeKeyDown={false}
      >
        <DialogAddLedgerEntry
          open={entryFormOpen}
          onClose={(refresh) => {
            setEntryFormOpen(false);
            if (refresh) {
              dispatch(fetchLedgers({ page, limit: pageSize, search: searchQuery, groupCategoryId, primaryGroupId, filterOwn }));
            }
          }}
        />
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete ledger <strong>{ledgerToDelete?.ledgerName}</strong>?
            <br /><br />
            This will soft delete the ledger. Historical journal entries will remain intact,
            but you won't be able to create new entries with this ledger.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Ledgers;
